// shop-frontend/src/utils/pushNotifications.js
// Web Push notification subscription manager for the shop app

const PROD_BASE = "https://moondala-one.onrender.com";

function getApiBase() {
  if (typeof process !== "undefined" && process.env) {
    const explicit = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || "";
    if (explicit) return explicit;
  }
  if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
    return "http://localhost:5000";
  }
  return PROD_BASE;
}

function getShopToken() {
  return localStorage.getItem("shopToken") || "";
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function getPermissionState() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { ok: false, reason: "Push notifications not supported" };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "Permission denied" };
  }

  try {
    const base = getApiBase();

    const vapidRes = await fetch(`${base}/api/push/vapid-key`);
    const vapidData = await vapidRes.json();
    if (!vapidData.ok) return { ok: false, reason: "Push not configured on server" };

    // Register push-capable service worker
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register("/push-sw.js");
      await navigator.serviceWorker.ready;
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidData.vapidPublicKey),
    });

    const token = getShopToken();
    const res = await fetch(`${base}/api/push/subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-shop-token": token,
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        app: "shop",
        deviceLabel: _getDeviceLabel(),
      }),
    });

    const data = await res.json();
    if (!data.ok) return { ok: false, reason: data.message || "Subscribe failed" };

    localStorage.setItem("shopPushSubscribed", "true");
    return { ok: true };
  } catch (err) {
    console.error("Push subscribe error:", err);
    return { ok: false, reason: err.message };
  }
}

export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return { ok: true };
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return { ok: true };
    await subscription.unsubscribe();
    const base = getApiBase();
    await fetch(`${base}/api/push/unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    }).catch(() => {});
    localStorage.removeItem("shopPushSubscribed");
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err.message };
  }
}

export async function isSubscribed() {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

function _getDeviceLabel() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "iOS Safari";
  if (/Android/.test(ua)) return "Android Chrome";
  if (/Windows/.test(ua)) return "Windows";
  if (/Mac/.test(ua)) return "Mac";
  return "Browser";
}
